import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { getEcho } from "@/lib/echo";

/**
 * Live floor + open bill refresh when KOT board updates (Reverb).
 */
export function useWaiterLive(branchId, { billId, onBillRefresh, onKotEvent } = {}) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState("connecting");
  const onBillRefreshRef = useRef(onBillRefresh);
  const onKotEventRef = useRef(onKotEvent);

  useEffect(() => {
    onBillRefreshRef.current = onBillRefresh;
  }, [onBillRefresh]);

  useEffect(() => {
    onKotEventRef.current = onKotEvent;
  }, [onKotEvent]);

  useEffect(() => {
    if (!branchId) {
      setStatus("offline");
      return undefined;
    }

    const echo = getEcho();
    if (!echo) {
      setStatus("offline");
      return undefined;
    }

    setStatus("connecting");
    const channelName = `kot-board.${branchId}`;
    const channel = echo.private(channelName);

    const onKotUpdated = (payload) => {
      queryClient.invalidateQueries({ queryKey: ["waiter-floor", branchId] });
      if (billId && onBillRefreshRef.current) {
        onBillRefreshRef.current();
      }
      if (onKotEventRef.current) {
        onKotEventRef.current(payload || {});
      }
    };

    channel.listen(".kot.updated", onKotUpdated);

    const connector = echo.connector?.pusher;
    if (connector) {
      const onConnected = () => setStatus("live");
      const onDisconnected = () => setStatus("reconnecting");
      const onFailed = () => setStatus("offline");

      connector.connection.bind("connected", onConnected);
      connector.connection.bind("disconnected", onDisconnected);
      connector.connection.bind("failed", onFailed);
      connector.connection.bind("unavailable", onFailed);

      if (connector.connection.state === "connected") {
        setStatus("live");
      }

      return () => {
        connector.connection.unbind("connected", onConnected);
        connector.connection.unbind("disconnected", onDisconnected);
        connector.connection.unbind("failed", onFailed);
        connector.connection.unbind("unavailable", onFailed);
        echo.leave(channelName);
      };
    }

    setStatus("live");

    return () => {
      echo.leave(channelName);
    };
  }, [branchId, billId, queryClient]);

  return status;
}
